import { useCallback } from 'react';

import { type DocNode } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { isSupportedStreamAction } from '@atlassian/conversation-assistant-action-helpers/utils';
import {
	AVP_CHART_CREATE_KEY,
	AVP_CHART_DELETE_KEY,
	AVP_CHART_UPDATE_KEY,
	AVP_DASHBOARD_REMOVE_ELEMENT_KEY,
	AVP_DASHBOARD_REMOVE_ROW_KEY,
	AVP_FILTER_EXPRESSION_CREATE_KEY,
	AVP_FILTER_EXPRESSION_DELETE_KEY,
	AVP_FILTER_EXPRESSION_UPDATE_KEY,
	isAvpChartCreateActionData,
	isAvpChartDeleteActionData,
	isAvpChartUpdateActionData,
	isAvpDashboardRemoveElementActionData,
	isAvpDashboardRemoveRowActionData,
	isAvpFilterExpressionCreateActionData,
	isAvpFilterExpressionDeleteActionData,
	isAvpFilterExpressionUpdateActionData,
} from '@atlassian/conversation-assistant-action-helpers/utils/avp-actions';
import {
	AIFC_ACTION_CLASSIFICATION_METADATA,
	AIFC_ADF_UPDATE_KEY,
	AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY,
	CONFLUENCE_AIFC_DATABASE_CREATE_KEY,
	CONFLUENCE_AIFC_DATABASE_UPDATE_KEY,
	CONFLUENCE_AIFC_DEEP_RESEARCH_PAGE_CREATE_KEY,
	CONFLUENCE_AIFC_PAGE_CREATE_KEY,
	CONFLUENCE_AIFC_SLIDES_CREATE_KEY,
	CONFLUENCE_AIFC_SLIDES_UPDATE_KEY,
	CONFLUENCE_AIFC_SPACE_CREATE_KEY,
	CONFLUENCE_AIFC_SPACE_UPDATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_TO_PAGE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	CONFLUENCE_WHITEBOARD_BRAINSTORMING_KEY,
	DYNAMIC_UI_CREATE_KEY,
	IMAGE_CREATE_KEY,
	isAIFCADFSetActionClassificationActionData,
	isAIFCADFUpdateActionData,
	isConfluenceAIFCDatabaseCreateActionData,
	isConfluenceAIFCDatabaseUpdateActionData,
	isConfluenceAIFCDeepResearchPageCreateActionData,
	isConfluenceAIFCDeepResearchPageCreateActionPrepare,
	isConfluenceAIFCPageCreateActionData,
	isConfluenceAIFCSlidesCreateActionData,
	isConfluenceAIFCSlidesUpdateActionData,
	isConfluenceAIFCSpaceCreateActionData,
	isConfluenceAIFCSpaceUpdateActionData,
	isConfluenceAIFCWhiteboardCreateActionData,
	isConfluenceAIFCWhiteboardUpdateActionData,
	isConfluenceWhiteboardBrainstormingActionData,
	isConfluenceWhiteboardDiagrammingActionData,
	isDynamicUiUpdateActionData,
	isImageCreateData,
	isImageUpdateActionData,
	isImageUpdatePrepareData,
	LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY,
	type ImageUpdatePrepareDataPayload,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import type { CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import {
	REMIX_EDIT_EXISTING_MARKS,
	remixEditExistingExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	isConfirmAction,
	type StreamActionClassification,
	type StreamActionComplete,
	type StreamActionPart,
	type StreamActionPrepare,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type MessageActionPayloadContext } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { getAvpDashboardClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/avp-dashboard-client';
import { getConfluenceDatabaseClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-database-client';
import { getConfluenceSlidesClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-slides-client';
import { getConfluenceEditorClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-smart-create-editor-client';
import { CONFLUENCE_SPACE_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-space-client';
import { getConfluenceWhiteboardClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-whiteboard-client';
import { ActionClassificationCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/action-classification-command';
import { AvpChartCreateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/avp-chart-create-command';
import { AvpChartDeleteCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/avp-chart-delete-command';
import { AvpChartUpdateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/avp-chart-update-command';
import { AvpDashboardRemoveElementCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/avp-dashboard-remove-element-command';
import { AvpDashboardRemoveRowCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/avp-dashboard-remove-row-command';
import { AvpFilterExpressionCreateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/avp-filter-expression-create-command';
import { AvpFilterExpressionDeleteCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/avp-filter-expression-delete-command';
import { AvpFilterExpressionUpdateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/avp-filter-expression-update-command';
import { ConfluenceDatabaseCreateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-create-database-commands';
import { ConfluencePageCreateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-create-page-command';
import { ConfluenceSlidesCreateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-create-slides-commands';
import { ConfluenceSpaceCreateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-create-space-command';
import { ConfluenceWhiteboardCreateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-create-whiteboard-commands';
import { ConfluenceDeepResearchPageCreateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-deep-research-page-create-command';
import { ConfluenceSubmitPromptOnPageCreateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-submit-prompt-on-page-create-command';
import { ConfluenceDatabaseUpdateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-database-command';
import { ConfluenceUpdatePageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';
import { ConfluenceSlidesUpdateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-slides-command';
import { ConfluenceSpaceUpdateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-space-command';
import { ConfluenceWhiteboardUpdateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-whiteboard-command';
import { ConfluenceWhiteboardBrainstormingCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-whiteboard-brainstorming-command';
import { ConfluenceWhiteboardDiagrammingCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-whiteboard-diagramming-command';
import {
	ReadContentCommand,
	type ReadContentCommandResult,
} from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';
import {
	ReadSpaceCommand,
	type ReadSpaceCommandResult,
} from '@atlassian/rovo-content-bridge-api-commands/commands/read-space-command';
import { RovoBroadcastConfirmedActionsCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/rovo-broadcast-confirmed-action-command';
import { type SerializableValue } from '@atlassian/rovo-content-bridge-api/command';

import { useGlobalConfig } from '../../../common/utils/global-config/useGlobalConfig';
import { useBridgeBroadcastActionCommand } from '../context/useBridgeBroadcastActionCommand';
import { useBridgeSendCommand } from '../context/useBridgeSendCommand';
import { broadcastStreamStatus } from '../streaming-lifecycle/broadcastStreamStatus';
import { type StreamLifecycleState } from '../streaming-lifecycle/StreamLifecycleState';
import { extractTextFromDocNode } from '../utils';

import { categorizeAifcDatabaseDocument } from './categorizeAifcDatabaseDocument';
import { getCurrentPageId } from './getCurrentPageId';

type SendBridgeCommandProps = {
	invocationId: string;
	traceId: string | undefined;
	requestId?: string;
	key: string;
	data: unknown;
	status?: string;
	prompt?: DocNode;
	isFinalResponse: boolean;
	singleInstrumentationId: string | undefined;
	agent?: { id: string; name: string; identityAccountId: string | null };
	/**
	 * The originating Confluence experience. Used to mark the
	 * ConfluenceUpdatePageCommand as an inline edit so the editor receiver only
	 * auto-opens the Post Stream Review "Review moment" for inline edits.
	 */
	experience?: CreationContextParams['experience'];
};

const cloneSerializableValue = (value: SerializableValue): SerializableValue => {
	if (value === undefined || value === null || typeof value !== 'object') {
		return value;
	}

	return typeof window !== 'undefined' && typeof window.structuredClone === 'function'
		? window.structuredClone(value)
		: JSON.parse(JSON.stringify(value));
};

export const useMessageStreamAction = (): {
	getContentBody: (options: {
		type: 'page' | 'whiteboard' | 'database' | 'dashboard' | 'slide';
		id: string | number;
		source?: string;
		prompt?: DocNode;
	}) => Promise<ReadContentCommandResult | undefined>;
	getStagingSpace: (id: string) => Promise<ReadSpaceCommandResult | undefined>;
	processMessageStreamAction: (
		invocationId: string,
		streamAction: StreamActionPart | StreamActionPrepare,
		traceId: string | undefined,
		requestId?: string,
		prompt?: DocNode,
		attributes?: {
			singleInstrumentationId?: string | undefined;
			experience?: CreationContextParams['experience'];
		},
		agent?: { id: string; name: string; identityAccountId: string | null },
	) => void;
	processMessageStreamComplete: (
		invocationId: string,
		streamAction: StreamActionComplete,
		traceId: string | undefined,
		requestId?: string,
		attributes?: {
			singleInstrumentationId?: string;
			experience?: CreationContextParams['experience'];
		},
	) => void;
	processMessageStreamError: (errorInfo: {
		actionKey?: string;
		errorType?: string;
		errorMessage?: string;
		traceId?: string;
	}) => void;
	processMessageStreamActionClassification: (
		invocationId: string,
		streamActionClassification: StreamActionClassification,
		traceId: string | undefined,
		requestId?: string,
		attributes?: { singleInstrumentationId?: string },
	) => void;
	broadcastStreamStatus: (
		status: StreamLifecycleState,
		streamId: string,
		content?: {
			id: string | number;
			type: 'page' | 'whiteboard' | 'jira' | 'database' | 'dashboard' | 'slide';
		},
	) => void;
	broadcastConfirmedActions: (invocationId: string, actions: MessageActionPayloadContext[]) => void;
} => {
	const { sendCommand } = useBridgeSendCommand();
	const { broadcastConfirmedActionCommand } = useBridgeBroadcastActionCommand();
	const { productKey, smartCreationContext } = useGlobalConfig();

	const sendBridgeCommand = useCallback(
		({
			invocationId,
			key,
			data,
			status,
			prompt,
			traceId,
			requestId,
			singleInstrumentationId,
			isFinalResponse = false,
			agent,
			experience,
		}: SendBridgeCommandProps) => {
			const resolveEditorReceiverId = (contentId?: string): string =>
				contentId ??
				(productKey !== 'confluence' && fg('platform_editor_maui_jira_updates_1')
					? smartCreationContext?.editor?.editorId
					: undefined) ??
				getCurrentPageId();

			try {
				switch (key) {
					case CONFLUENCE_AIFC_WHITEBOARD_TO_PAGE_KEY:
					case CONFLUENCE_AIFC_PAGE_CREATE_KEY:
						if (isConfluenceAIFCPageCreateActionData(data)) {
							sendCommand(ConfluencePageCreateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getConfluenceEditorClientId(data.contentId),
								groupId: invocationId,
								traceId,
								singleInstrumentationId,
							});

							if (status === 'PREPARING' && prompt) {
								sendCommand(ConfluenceSubmitPromptOnPageCreateCommand, {
									receiverId: getConfluenceEditorClientId(data.contentId),
									userPrompt: extractTextFromDocNode(prompt),
								});
							}
						}
						break;
					case AIFC_ADF_UPDATE_KEY:
						if (isAIFCADFUpdateActionData(data)) {
							sendCommand(ConfluenceUpdatePageCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getConfluenceEditorClientId(data.contentId ?? getCurrentPageId()),
								groupId: invocationId,
								traceId,
								requestId,
								singleInstrumentationId,
								agentId: agent?.id,
								agentName: agent?.name,
								agentIdentityAccountId: agent?.identityAccountId,
								// Mark inline edits so the editor receiver only
								// auto-opens the Post Stream Review "Review moment" for them.
								isInlineEdit: experience === 'inline_edit',
							});
						}
						break;
					case DYNAMIC_UI_CREATE_KEY:
					case IMAGE_CREATE_KEY:
						if (
							isDynamicUiUpdateActionData(data) ||
							isImageUpdateActionData(data) ||
							isImageCreateData(data)
						) {
							sendCommand(ConfluenceUpdatePageCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getConfluenceEditorClientId(resolveEditorReceiverId(data.contentId)),
								groupId: invocationId,
								traceId,
								requestId,
								singleInstrumentationId,
							});
						} else if (
							key === IMAGE_CREATE_KEY &&
							isImageUpdatePrepareData(data) &&
							!isFinalResponse
						) {
							// ACTION_PREPARE: send partial data to trigger loading state on the existing image
							sendCommand(ConfluenceUpdatePageCommand, {
								key: key,
								data: data as ImageUpdatePrepareDataPayload,
								isStreamComplete: false,
								receiverId: getConfluenceEditorClientId(resolveEditorReceiverId(data.contentId)),
								groupId: invocationId,
								traceId,
								requestId,
								singleInstrumentationId,
							});
						}
						break;

					case AIFC_ACTION_CLASSIFICATION_METADATA:
						if (fg('platform_editor_ai_promptversion_in_analytics')) {
							if (isAIFCADFSetActionClassificationActionData(data)) {
								sendCommand(ActionClassificationCommand, {
									data: data,
									receiverId: getConfluenceEditorClientId(data.contentId),
									groupId: invocationId,
								});
							}
						}
						break;
					case CONFLUENCE_AIFC_DEEP_RESEARCH_PAGE_CREATE_KEY:
						if (isConfluenceAIFCDeepResearchPageCreateActionData(data)) {
							sendCommand(ConfluenceDeepResearchPageCreateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getConfluenceEditorClientId(data.contentId),
								groupId: invocationId,
							});
						}
						// TODO: Remove isConfluenceAIFCDeepResearchPageCreateActionPrepare condition and type once BE fix is in
						// https://hello.jira.atlassian.cloud/browse/CCI-12965
						if (
							isConfluenceAIFCDeepResearchPageCreateActionPrepare(data) ||
							isConfluenceAIFCDeepResearchPageCreateActionData(data)
						) {
							if (status === 'PREPARING' && prompt) {
								sendCommand(ConfluenceSubmitPromptOnPageCreateCommand, {
									receiverId: getConfluenceEditorClientId(data?.contentId),
									userPrompt: extractTextFromDocNode(prompt),
								});
							}
						}
						break;
					case CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY:
						if (isConfluenceAIFCWhiteboardCreateActionData(data)) {
							sendCommand(ConfluenceWhiteboardCreateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getConfluenceWhiteboardClientId(data.contentId),
								groupId: invocationId,
								singleInstrumentationID: singleInstrumentationId,
							});
						}
						break;
					case CONFLUENCE_AIFC_SLIDES_CREATE_KEY:
						if (isConfluenceAIFCSlidesCreateActionData(data)) {
							sendCommand(ConfluenceSlidesCreateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getConfluenceSlidesClientId(data.contentId),
								groupId: invocationId,
								singleInstrumentationID: singleInstrumentationId,
							});
						}
						break;
					case CONFLUENCE_AIFC_SLIDES_UPDATE_KEY:
						if (isConfluenceAIFCSlidesUpdateActionData(data)) {
							sendCommand(ConfluenceSlidesUpdateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getConfluenceSlidesClientId(data.contentId),
								groupId: invocationId,
								singleInstrumentationID: singleInstrumentationId,
							});
						}
						break;
					case CONFLUENCE_AIFC_DATABASE_CREATE_KEY:
						if (isConfluenceAIFCDatabaseCreateActionData(data)) {
							// Shape-only blank-row diagnostic; does not change flow.
							if (isFinalResponse && fg('platform_editor_aifc_create_stream_debug')) {
								const diagnostics = categorizeAifcDatabaseDocument(data.document, data.contentId);
								if (diagnostics.category !== 'ok') {
									logException({
										exception: new Error(
											`AIFC database create finalized with problematic document: ${diagnostics.category}`,
										),
										name: 'AifcDatabaseCreateDocumentIssue',
										tags: diagnostics,
									});
								}
							}
							sendCommand(ConfluenceDatabaseCreateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getConfluenceDatabaseClientId(data.contentId),
								groupId: invocationId,
								singleInstrumentationID: singleInstrumentationId,
							});
						}
						break;
					case CONFLUENCE_AIFC_DATABASE_UPDATE_KEY:
						if (isConfluenceAIFCDatabaseUpdateActionData(data)) {
							sendCommand(ConfluenceDatabaseUpdateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getConfluenceDatabaseClientId(data.contentId),
								groupId: invocationId,
								singleInstrumentationID: singleInstrumentationId,
							});
						}
						break;
					case CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY:
						if (isConfluenceAIFCWhiteboardUpdateActionData(data)) {
							sendCommand(ConfluenceWhiteboardUpdateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getConfluenceWhiteboardClientId(data.contentId),
								groupId: invocationId,
								singleInstrumentationID: singleInstrumentationId,
							});
						}
						break;
					case CONFLUENCE_WHITEBOARD_BRAINSTORMING_KEY:
						if (isConfluenceWhiteboardBrainstormingActionData(data)) {
							sendCommand(ConfluenceWhiteboardBrainstormingCommand, {
								key: key,
								data: data,
								status, // Brainst. agent sends `status=SUCCESS` in the last message of the stream to mark end of data
								isStreamComplete: isFinalResponse,
								receiverId: getConfluenceWhiteboardClientId(data.contentId),
								groupId: invocationId,
							});
						}
						break;
					case LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY:
						if (isConfluenceWhiteboardDiagrammingActionData(data)) {
							sendCommand(ConfluenceWhiteboardDiagrammingCommand, {
								key: key,
								data: data,
								status, // Diag. agent sends `status=SUCCESS` in the last message of the stream to mark end of data
								isStreamComplete: isFinalResponse,
								receiverId: getConfluenceWhiteboardClientId(data.contentId),
								groupId: invocationId,
							});
						}
						break;
					case AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY:
						// This new key goes through the new AIFC flow for diagramming
						// Before we were going through the legacy flow, LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY
						if (isConfluenceAIFCWhiteboardCreateActionData(data)) {
							sendCommand(ConfluenceWhiteboardCreateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getConfluenceWhiteboardClientId(data.contentId),
								groupId: invocationId,
								singleInstrumentationID: singleInstrumentationId,
							});
						}
						break;
					case CONFLUENCE_AIFC_SPACE_CREATE_KEY:
						if (isConfluenceAIFCSpaceCreateActionData(data)) {
							sendCommand(ConfluenceSpaceCreateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: CONFLUENCE_SPACE_CLIENT_ID,
								groupId: invocationId,
							});
						}
						break;
					case CONFLUENCE_AIFC_SPACE_UPDATE_KEY:
						if (isConfluenceAIFCSpaceUpdateActionData(data)) {
							sendCommand(ConfluenceSpaceUpdateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: CONFLUENCE_SPACE_CLIENT_ID,
								groupId: invocationId,
							});
						}
						break;
					case AVP_CHART_CREATE_KEY:
						if (isAvpChartCreateActionData(data)) {
							sendCommand(AvpChartCreateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getAvpDashboardClientId(data.dashboardAri),
								groupId: invocationId,
							});
						}
						break;
					case AVP_CHART_DELETE_KEY:
						if (isAvpChartDeleteActionData(data)) {
							sendCommand(AvpChartDeleteCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getAvpDashboardClientId(data.dashboardAri),
								groupId: invocationId,
							});
						}
						break;
					case AVP_CHART_UPDATE_KEY:
						if (isAvpChartUpdateActionData(data)) {
							sendCommand(AvpChartUpdateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getAvpDashboardClientId(data.dashboardAri),
								groupId: invocationId,
							});
						}
						break;
					case AVP_DASHBOARD_REMOVE_ROW_KEY:
						if (isAvpDashboardRemoveRowActionData(data)) {
							sendCommand(AvpDashboardRemoveRowCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getAvpDashboardClientId(data.dashboardAri),
								groupId: invocationId,
							});
						}
						break;
					case AVP_DASHBOARD_REMOVE_ELEMENT_KEY:
						if (isAvpDashboardRemoveElementActionData(data)) {
							sendCommand(AvpDashboardRemoveElementCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getAvpDashboardClientId(data.dashboardAri),
								groupId: invocationId,
							});
						}
						break;
					case AVP_FILTER_EXPRESSION_CREATE_KEY:
						if (isAvpFilterExpressionCreateActionData(data)) {
							sendCommand(AvpFilterExpressionCreateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getAvpDashboardClientId(data.dashboardAri),
								groupId: invocationId,
							});
						}
						break;
					case AVP_FILTER_EXPRESSION_DELETE_KEY:
						if (isAvpFilterExpressionDeleteActionData(data)) {
							sendCommand(AvpFilterExpressionDeleteCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getAvpDashboardClientId(data.dashboardAri),
								groupId: invocationId,
							});
						}
						break;
					case AVP_FILTER_EXPRESSION_UPDATE_KEY:
						if (isAvpFilterExpressionUpdateActionData(data)) {
							sendCommand(AvpFilterExpressionUpdateCommand, {
								key: key,
								data: data,
								isStreamComplete: isFinalResponse,
								receiverId: getAvpDashboardClientId(data.dashboardAri),
								groupId: invocationId,
							});
						}
						break;
					default:
						logException({
							exception: new Error(`Unsupported action key: ${key}`),
							name: 'SendBridgeCommandUnsupportedActionKey',
						});
						break;
				}
			} catch (error) {
				logException({
					exception: error,
					name: 'SendBridgeCommandError',
				});
			}
		},
		[sendCommand, productKey, smartCreationContext],
	);

	// receiver
	const processMessageStreamAction = useCallback(
		(
			invocationId: string,
			streamAction: StreamActionPart | StreamActionPrepare,
			traceId: string | undefined,
			requestId?: string,
			prompt?: DocNode,
			attributes?: {
				singleInstrumentationId?: string | undefined;
				experience?: CreationContextParams['experience'];
			},
			agent?: { id: string; name: string; identityAccountId: string | null },
		): void => {
			const { message: action } = streamAction;
			if (!isSupportedStreamAction(action)) {
				return;
			}

			sendBridgeCommand({
				invocationId,
				key: action.key,
				data: action.data,
				status: action.status,
				prompt,
				isFinalResponse: false,
				traceId,
				requestId,
				singleInstrumentationId: attributes?.singleInstrumentationId,
				agent,
				experience: attributes?.experience,
			});
		},
		[sendBridgeCommand],
	);

	const processMessageStreamComplete = useCallback(
		(
			invocationId: string,
			streamAction: StreamActionComplete,
			traceId: string | undefined,
			requestId?: string,
			attributes?: {
				singleInstrumentationId?: string;
				experience?: CreationContextParams['experience'];
			},
		): void => {
			const { message: action } = streamAction;
			if (!isSupportedStreamAction(action)) {
				return;
			}

			// metadata is only available on ACTION_COMPLETE for editing remix.
			if (action.key === 'dynamic-ui-create' || action.key === 'image-create') {
				remixEditExistingExperience.mark(REMIX_EDIT_EXISTING_MARKS.ACTION_COMPLETE_RECEIVED);
				const actionData = action.data as
					| {
							dynamicUiSubtype?: string;
							contentType?: string;
					  }
					| undefined;
				// marking image create to infographic to be same as generating preview
				if (action.key === 'image-create') {
					remixEditExistingExperience.addMetadata({
						remixType: 'infographic',
					});
				} else {
					remixEditExistingExperience.addMetadata({
						remixType: actionData?.contentType,
						remixSubtype: actionData?.dynamicUiSubtype,
					});
				}
			}

			sendBridgeCommand({
				invocationId,
				key: action.key,
				data: action.data,
				isFinalResponse: true,
				traceId,
				requestId,
				singleInstrumentationId: attributes?.singleInstrumentationId,
				experience: attributes?.experience,
			});
		},
		[sendBridgeCommand],
	);

	const processMessageStreamError = useCallback(
		(errorInfo: {
			actionKey?: string;
			errorType?: string;
			errorMessage?: string;
			traceId?: string;
		}): void => {
			// Fail the remix-edit-existing UFO experience when the stream errors.
			// .failure() is a no-op on a non-started experience, so this is safe
			// for non-remix-edit-existing flows.
			if (errorInfo.actionKey === 'dynamic-ui-create' || errorInfo.actionKey === 'image-create') {
				remixEditExistingExperience.failure({
					metadata: {
						errorType: errorInfo.errorType,
						errorMessage: errorInfo.errorMessage,
						traceId: errorInfo.traceId,
					},
				});
			}
		},
		[],
	);

	const processMessageStreamActionClassification = useCallback(
		(
			invocationId: string,
			streamActionClassification: StreamActionClassification,
			traceId: string | undefined,
			requestId?: string,
			attributes?: { singleInstrumentationId?: string },
		): void => {
			if (
				streamActionClassification.message.metadata &&
				fg('platform_editor_ai_promptversion_in_analytics')
			) {
				sendBridgeCommand({
					invocationId,
					key: AIFC_ACTION_CLASSIFICATION_METADATA,
					data: streamActionClassification.message.metadata,
					isFinalResponse: false,
					traceId,
					requestId,
					singleInstrumentationId: attributes?.singleInstrumentationId,
				});
			}
		},
		[sendBridgeCommand],
	);

	/**
	 * Broadcasts confirmed message actions to subscribed clients.
	 *
	 * Iterates over the provided list of actions, and for each action that is confirmed
	 * (as determined by `isConfirmAction`), it sends a broadcast command using
	 * `broadcastConfirmedActionCommand`. The broadcast includes the action's key and
	 * the provided invocation ID as the group identifier.
	 *
	 * @param invocationId - The unique identifier for the current invocation or group.
	 * @param actions - An array of message action payload contexts to be checked and broadcasted if confirmed.
	 */
	const broadcastConfirmedActions = useCallback(
		(invocationId: string, actions: MessageActionPayloadContext[]): void => {
			for (const action of actions) {
				if (isConfirmAction(action)) {
					broadcastConfirmedActionCommand(RovoBroadcastConfirmedActionsCommand, {
						key: action.key,
						groupId: invocationId,
					});
				}
			}
		},
		[broadcastConfirmedActionCommand],
	);

	const getContentBody = useCallback(
		async ({
			type,
			id,
			source,
			prompt,
		}: {
			type: 'page' | 'whiteboard' | 'database' | 'dashboard' | 'slide';
			id: string | number;
			source?: string;
			prompt?: DocNode;
		}): Promise<ReadContentCommandResult | undefined> => {
			const identifier = String(id);
			let receiverId;
			switch (type) {
				case 'page':
					receiverId = getConfluenceEditorClientId(identifier);
					break;
				case 'whiteboard':
					receiverId = getConfluenceWhiteboardClientId(identifier);
					break;
				case 'slide':
					receiverId = getConfluenceSlidesClientId(identifier);
					break;
				case 'database':
					receiverId = getConfluenceDatabaseClientId(identifier);
					break;
				case 'dashboard':
					receiverId = getAvpDashboardClientId(identifier);
					break;
				default:
					receiverId = getConfluenceEditorClientId(identifier);
			}
			const shouldSendFastPathInputs = expValEquals(
				'platform_editor_ai_minion_skip_orchestrator',
				'isEnabled',
				true,
			);
			const shouldSendSource = source !== undefined && shouldSendFastPathInputs;
			const shouldSendPrompt = prompt !== undefined && shouldSendFastPathInputs;

			const invocation = sendCommand(ReadContentCommand, {
				receiverId,
				buffer: false,
				...(shouldSendSource ? { source } : {}),
				...(shouldSendPrompt
					? {
							// eslint-disable-next-line @atlaskit/platform/no-double-type-assertions -- DocNode is serializable ADF, but its type does not satisfy the bridge SerializableValue index signature.
							prompt: cloneSerializableValue(prompt as unknown as SerializableValue),
						}
					: {}),
			});
			return invocation.result();
		},
		[sendCommand],
	);

	const getStagingSpace = useCallback(
		(id: string): Promise<ReadSpaceCommandResult | undefined> =>
			sendCommand(ReadSpaceCommand, {
				id,
				receiverId: CONFLUENCE_SPACE_CLIENT_ID,
				buffer: false,
			}).result(),
		[sendCommand],
	);

	return {
		getContentBody,
		getStagingSpace,
		processMessageStreamAction,
		processMessageStreamComplete,
		processMessageStreamError,
		processMessageStreamActionClassification,
		broadcastStreamStatus,
		broadcastConfirmedActions,
	};
};
